/**
 * HeadWithdrawHistory Component
 * 본사 입출금현황 페이지 - 협력사 입출금 내역 조회 및 입금/출금 신청
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHeadWithdrawData } from '../hooks/useHeadWithdrawData';
import { useDelayedLoading } from '@/shared/hooks/useDelayedLoading';
import { useUserInfo } from '@/shared/hooks/useCrudOperations';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import Loading from '@/shared/components/feedback/Loading';
import HeadWithdrawModal from './HeadWithdrawModal';
import HeadDepositModal from './HeadDepositModal';
import FilterSection from './FilterSection';
import HistoryAccordion, { ViewMode } from './HistoryAccordion';
import SummarySection from './SummarySection';
import {
    WithdrawHistoryFilters,
    createDefaultFilters
} from './withdrawHistory.helpers';
import '@/shared/styles/common-table.css';
import '../styles/HeadWithdrawHistory.css';

/** 필터 저장 키 */
const FILTER_STORAGE_KEY = 'headWithdrawFilters';

const HeadWithdrawHistory: React.FC = () => {
    // ======== Hooks ========
    const { getUserInfo } = useUserInfo();
    const userInfo = getUserInfo();
    const isHeadUser = userInfo?.userType === 'HEADQUARTERS';
    const isSupplierUser = userInfo?.userType === 'SUPPLIER';
    // 협력사 사용자의 협력사 ID: suppId가 없으면 userId를 사용 (협력사 계정은 userId가 협력사 ID인 경우가 있음)
    const userSuppId = userInfo?.suppId || (isSupplierUser ? userInfo?.userId : undefined);

    // 디버깅: 협력사 사용자 정보 확인
    console.log('[HeadWithdrawHistory] userInfo:', userInfo);
    console.log('[HeadWithdrawHistory] isSupplierUser:', isSupplierUser, 'userSuppId:', userSuppId);

    const {
        histories,
        currentBalanceByRider,
        currentBalanceBySupplier,
        withdrawableAmount,
        loading,
        withdrawFee,
        depositFee,
        accountInfo,
        supplierOptions,
        riderOptions,
        fetchHistories,
        fetchWithdrawableAmount,
        fetchAccountInfo,
        fetchSupplierOptions,
        fetchRiderOptions,
        requestWithdraw,
        requestDeposit,
        downloadExcel,
    } = useHeadWithdrawData();

    const showLoading = useDelayedLoading(loading);

    // 뷰 모드 상태 (협력사별 또는 라이더별)
    const [viewMode, setViewMode] = useState<ViewMode>('supplier');

    // ======== State ========
    /** 검색 필터 상태 */
    const [filters, setFilters] = useState<WithdrawHistoryFilters>(() => {
        const saved = loadFilters<WithdrawHistoryFilters>(FILTER_STORAGE_KEY);
        return saved || createDefaultFilters();
    });

    /** 출금 모달 열림 상태 */
    const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

    /** 입금 모달 열림 상태 */
    const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);

    /** 초기화 완료 여부 */
    const hasInitialized = useRef(false);

    // ======== Effects ========
    /** 출금 모달 열릴 때 출금가능금액 조회 */
    useEffect(() => {
        if (isWithdrawModalOpen && isHeadUser) {
            fetchWithdrawableAmount().catch(() => { /* 에러는 훅에서 처리 */ });
        }
    }, [isWithdrawModalOpen, isHeadUser, fetchWithdrawableAmount]);

    /** 입금 모달 열릴 때 계좌 정보 조회 */
    useEffect(() => {
        if (isDepositModalOpen && isHeadUser) {
            fetchAccountInfo(true).catch(() => { /* 에러는 훅에서 처리 */ });
        }
    }, [isDepositModalOpen, isHeadUser, fetchAccountInfo]);

    /** 협력사 옵션 목록 조회 (필터 날짜 변경 시)- 본사 사용자만 */
    useEffect(() => {
        if (filters.startDate && filters.endDate && !isSupplierUser) {
            fetchSupplierOptions(filters.startDate, filters.endDate).catch(() => { /* 에러는 훅에서 처리 */ });
            // 날짜 변경 시 라이더 목록도 조회 (협력사 선택 여부와 관계없이)
            fetchRiderOptions(filters.startDate, filters.endDate, filters.supplierName || '').catch(() => { /* 에러는 훅에서 처리 */ });
        }
    }, [filters.startDate, filters.endDate, fetchSupplierOptions, fetchRiderOptions, filters.supplierName, isSupplierUser]);

    /** 협력사가 선택되어 있으면 라이더 목록 자동 조회 (협력사 사용자는 자신의 협력사 ID로 조회)*/
    useEffect(() => {
        if (filters.startDate && filters.endDate) {
            const supplierNameToUse = isSupplierUser ? userSuppId : (filters.supplierName || '');
            fetchRiderOptions(filters.startDate, filters.endDate, supplierNameToUse || '').catch(() => { /* 에러는 훅에서 처리 */ });
        }
    }, [filters.supplierName, filters.startDate, filters.endDate, fetchRiderOptions, isSupplierUser, userSuppId]);

    /** 초기 데이터 로드 */
    useEffect(() => {
        if (!hasInitialized.current && userInfo) {
            hasInitialized.current = true;
            const savedFilters = loadFilters<WithdrawHistoryFilters>(FILTER_STORAGE_KEY);
            const filtersToUse = savedFilters || filters;

            // 협력사 사용자일 경우 자신의 suppId로 필터링 (supplierName은 비워서 suppId로만 필터링)
            const supplierIdToUse = isSupplierUser ? userSuppId : filtersToUse.supplierId;
            const supplierNameToUse = isSupplierUser ? undefined : filtersToUse.supplierName;

            // 디버깅: API 호출 전 파라미터 확인
            console.log('[HeadWithdrawHistory] fetchHistories 호출:', {
                isSupplierUser,
                userSuppId,
                supplierIdToUse,
                supplierNameToUse
            });

            fetchHistories(
                filtersToUse.startDate,
                filtersToUse.endDate,
                undefined,
                undefined,
                supplierIdToUse,
                supplierNameToUse,
                filtersToUse.riderId,
                filtersToUse.riderName
            ).catch(() => { /* 에러는 훅에서 처리 */ });
            // 예치금 잔액 조회
            fetchWithdrawableAmount().catch(() => { /* 에러는 훅에서 처리 */ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo, isSupplierUser, userSuppId]);

    // ======== Event Handlers ========
    /** 검색 실행 - 필터 저장 후 내역 조회 */
    const handleSearch = useCallback(() => {
        // 협력사 사용자일 경우 자신의 suppId로 필터링 (supplierName은 비워서 suppId로만 필터링)
        const supplierIdToUse = isSupplierUser ? userSuppId : filters.supplierId;
        const supplierNameToUse = isSupplierUser ? undefined : filters.supplierName;

        fetchHistories(
            filters.startDate,
            filters.endDate,
            undefined,
            undefined,
            supplierIdToUse,
            supplierNameToUse,
            filters.riderId,
            filters.riderName
        );
        saveFilters(FILTER_STORAGE_KEY, filters);
    }, [filters, fetchHistories, isSupplierUser, userSuppId]);

    /** 출금 신청 처리 */
    const handleRequestWithdraw = useCallback(async (amount: number, memo?: string) => {
        if (!userInfo?.userId) return;

        const result = await requestWithdraw(userInfo.userId, amount, memo);
        if (result) {
            setIsWithdrawModalOpen(false);
            fetchHistories(
                filters.startDate,
                filters.endDate,
                undefined,
                undefined,
                filters.supplierId,
                filters.supplierName,
                filters.riderId,
                filters.riderName
            );
        }
    }, [userInfo?.userId, requestWithdraw, filters, fetchHistories]);

    /** 입금 신청 처리 */
    const handleRequestDeposit = useCallback(async (amount: number, memo?: string) => {
        if (!userInfo?.userType) return;

        const result = await requestDeposit(userInfo.userType, amount, memo);
        if (result) {
            setIsDepositModalOpen(false);
            fetchHistories(
                filters.startDate,
                filters.endDate,
                undefined,
                undefined,
                filters.supplierId,
                filters.supplierName,
                filters.riderId,
                filters.riderName
            );
        }
    }, [userInfo?.userType, requestDeposit, filters, fetchHistories]);

    /** 출금 모달 열기 */
    const handleWithdrawModalOpen = useCallback(() => {
        setIsWithdrawModalOpen(true);
    }, []);

    /** 엑셀 다운로드 */
    const handleExcelDownload = useCallback(() => {
        downloadExcel(
            filters.startDate,
            filters.endDate,
            undefined,
            undefined,
            filters.supplierId,
            filters.supplierName,
            filters.riderId,
            filters.riderName
        );
    }, [downloadExcel, filters]);

    // ======== Render ========
    if (showLoading) {
        return <Loading />;
    }

    return (
        <div className="head-withdraw-history">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">입출금 내역</h1>
            </div>

            {/* 검색 필터 섹션 */}
            <FilterSection
                filters={filters}
                isHeadUser={isHeadUser}
                isSupplierUser={isSupplierUser}
                supplierOptions={supplierOptions}
                riderOptions={riderOptions}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onWithdrawClick={handleWithdrawModalOpen}
                onSupplierChange={(fromDate, toDate, supplierName) => {
                    fetchRiderOptions(fromDate, toDate, supplierName || '');
                }}
                onExcelDownload={handleExcelDownload}
            />

            {/* 합계 섹션 */}
            <SummarySection
                histories={histories}
                withdrawableAmount={withdrawableAmount}
            />

            {/* 검색 결과 건수 */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                총 <strong>{(histories || []).length.toLocaleString()}건</strong>
            </div>

            {/* 입출금 내역 아코디언 */}
            <HistoryAccordion
                histories={histories || []}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isSupplierUser={isSupplierUser}
                userSuppId={userSuppId}
                currentBalanceByRider={currentBalanceByRider}
                currentBalanceBySupplier={currentBalanceBySupplier}
            />

            {/* 입금신청 모달 */}
            {isHeadUser && isDepositModalOpen && (
                <HeadDepositModal
                    withdrawableAmount={withdrawableAmount}
                    depositFee={depositFee}
                    accountInfo={accountInfo}
                    onClose={() => setIsDepositModalOpen(false)}
                    onRequest={handleRequestDeposit}
                />
            )}

            {/* 출금신청 모달 */}
            {isHeadUser && isWithdrawModalOpen && (
                <HeadWithdrawModal
                    withdrawableAmount={withdrawableAmount}
                    withdrawFee={withdrawFee}
                    accountInfo={accountInfo}
                    onClose={() => setIsWithdrawModalOpen(false)}
                    onRequest={handleRequestWithdraw}
                />
            )}
        </div>
    );
};

export default HeadWithdrawHistory;
